<template>
    <div
        class="tw-border-0 tw-border-l-4 tw-border-solid tw-border-gray-100 tw-pl-5 tw-ml-3 tw-mb-2 tw-mt-4"
    >
        <div
            v-for="comment in parsedComments"
            :key="comment._id"
            class="tw-flex tw-space-x-4 mouseover-parent"
        >
            <span
                data-test-class="meeting-talking-point-comment-content"
                class="tw-flex-1 tw-group"
            >
                <l-input-text
                    v-if="editingComment == comment._id"
                    ref="comment"
                    v-model="comment.content"
                    data-test-class="input-edit-comment"
                    type="text"
                    optional
                    :label="''"
                    :placeholder="$t(`meetings.placeholderTalkingPointComments`)"
                    :input-class="'white-bg'"
                    style="margin-top: 0px"
                    tabindex="0"
                    @keyup.enter="
                        () => {
                            update(comment);
                            editingComment = '';
                        }
                    "
                />
                <span
                    v-else
                    @click="toggleEditComment(comment)"
                    @keyup.enter="toggleEditComment(comment)"
                >
                    <l-text
                        :text="comment.content"
                        :class="{ 'tw-cursor-pointer': comment.sender._id === user._id }"
                        style="overflow-wrap: break-word;"
                    />

                    <div
                        class="tw-opacity-0 group-hover:tw-opacity-100 tw-block tw-transition-all tw-duration-200 tw-text-gray-400 tw-leading-none tw-text-xxs"
                    >
                        <span v-if="comment.sender.firstname" class="tw-inline-block">{{
                            comment.sender.firstname
                        }}</span>
                        <time
                            v-if="comment.created_at"
                            class="tw-leading-none tw-ml-1 tw-inline-block"
                            :datetime="comment.created_at"
                        >
                            - {{ comment.created_at | moment("LL") }}</time
                        >
                    </div>
                </span>
            </span>

            <span class="tw-flex-grow-0 talking-point-icon-placeholder"></span>
            <span class="tw-flex-grow-0 talking-point-icon-placeholder"></span>

            <span class="tw-flex-grow-0 talking-point-icon-placeholder">
                <span
                    v-if="comment.sender._id === user._id"
                    v-b-popover.hover.right="{
                        content: $t('meetings.labelRemove'),
                    }"
                    data-test-class="meeting-talking-point-comment-delete"
                    tabindex="0"
                    @click="
                        () => {
                            remove(comment);
                            editingComment = '';
                        }
                    "
                    @keyup.enter="
                        () => {
                            remove(comment);
                            editingComment = '';
                        }
                    "
                >
                    <i class="fal fa-times tw-text-gray-400 clickable mouseover"></i>
                </span>
            </span>

            <span
                v-b-popover.hover.bottomright="{
                    content: `${comment.sender.firstname} ${comment.sender.lastname}`,
                }"
                class="tw-flex-grow-0"
            >
                <l-profile-picture :user="comment.sender" size="xs" />
            </span>
        </div>

        <div class="tw-mt-2">
            <l-input-text
                ref="comment"
                v-model="content"
                type="text"
                optional
                :label="''"
                :placeholder="$t(`meetings.placeholderTalkingPointComments`)"
                :input-class="'white-bg'"
                data-test-class="meeting-talking-point-comment"
                style="margin-top: 0px"
                tabindex="0"
                size="s"
                @keyup.enter="create()"
            />
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import ObjectId from "bson-objectid";

import LInputText from "@/components/LInputText";
import LProfilePicture from "@/components/LProfilePicture";
import LText from "@/components/LText";

export default {
    components: {
        LInputText,
        LProfilePicture,
        LText,
    },
    props: {
        comments: {
            type: Array,
            required: true,
        },
        meetingId: {
            type: String,
            required: true,
        },
        actionItem: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            content: "",
            editingComment: "",
            parsedComments: [], // Needed to support real-time updates with polling
        };
    },
    computed: {
        ...mapGetters({
            user: "users/current/user",
        }),
    },
    watch: {
        // Needed to support real-time updates with polling
        comments: {
            immediate: true,
            handler(newComments, previousComments) {
                this.parsedComments = newComments;

                if (this.editingComment) {
                    const newCommentIndex = newComments.findIndex(
                        c => c._id === this.editingComment,
                    );
                    const previousComment = previousComments.find(
                        c => c._id === this.editingComment,
                    );

                    if (newCommentIndex > -1 && previousComment) {
                        this.parsedComments[newCommentIndex].content = previousComment.content;
                    }
                }
            },
        },
    },
    methods: {
        create() {
            const request = this.$store.dispatch({
                type: "meetings/meetings/createComment",
                payload: {
                    commentId: ObjectId().toString(),
                    commentContent: this.content,
                    actionItemId: this.actionItem,
                    meetingId: this.meetingId,
                },
            });

            // We can update before waiting for the request as the update is committed synchronously
            // into the store.
            this.$emit("update", request);

            this.content = "";
        },
        update(comment) {
            if (!comment.content) {
                // If the content is empty, treat as deleting the comment
                return this.remove(comment);
            }

            const request = this.$store.dispatch({
                type: "meetings/meetings/updateComment",
                payload: {
                    commentContent: comment.content,
                    meetingId: this.meetingId,
                    actionItemId: this.actionItem,
                    commentId: comment._id,
                },
            });

            // We can update before waiting for the request as the update is committed synchronously
            // into the store.
            this.$emit("update", request);
        },
        remove(comment) {
            const request = this.$store.dispatch({
                type: "meetings/meetings/deleteComment",
                payload: {
                    commentId: comment._id,
                    actionItemId: this.actionItem,
                    meetingId: this.meetingId,
                },
            });

            // We can update before waiting for the request as the update is committed synchronously
            // into the store.
            this.$emit("update", request);
        },
        toggleEditComment(comment) {
            if (comment.sender._id !== this.user._id) {
                return;
            }
            this.editingComment = comment._id;
        },
    },
};
</script>
